import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { getAnnouncements } from '../services/announcementService';

const HeroSection = styled.section`
  min-height: 78vh;
  display: flex;
  align-items: center;
  background:
    linear-gradient(
      90deg,
      rgba(29, 22, 19, 0.78) 0%,
      rgba(29, 22, 19, 0.62) 45%,
      rgba(29, 22, 19, 0.28) 100%
    ),
    url('/images/gallery/bild1.jpg') center/cover no-repeat;
  color: #fff;

  @media (max-width: 768px) {
    min-height: calc(100vh - 70px);
    align-items: flex-start;
    background:
      linear-gradient(
        180deg,
        rgba(22, 16, 14, 0.84) 0%,
        rgba(22, 16, 14, 0.68) 42%,
        rgba(22, 16, 14, 0.82) 100%
      ),
      url('/images/gallery/bild1.jpg') center top/contain no-repeat;
    background-color: #1d1613;
  }

  @media (max-width: 420px) {
    background:
      linear-gradient(
        180deg,
        rgba(22, 16, 14, 0.86) 0%,
        rgba(22, 16, 14, 0.72) 45%,
        rgba(22, 16, 14, 0.84) 100%
      ),
      url('/images/gallery/bild1.jpg') center top/contain no-repeat;
    background-color: #1d1613;
  }
`;

const HeroInner = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 5rem 1.5rem;

  @media (max-width: 768px) {
    padding: 3.2rem 1.35rem 2rem;
  }

  @media (max-width: 420px) {
    padding: 2.7rem 1.15rem 1.8rem;
  }
`;

const ContentBox = styled.div`
  max-width: 720px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const Eyebrow = styled.span`
  display: inline-block;
  margin-bottom: 1rem;
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(4px);
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.02em;

  @media (max-width: 420px) {
    font-size: 0.82rem;
    padding: 0.4rem 0.75rem;
  }
`;

const Title = styled.h1`
  font-size: clamp(2.6rem, 6vw, 5.4rem);
  line-height: 0.98;
  margin-bottom: 1.1rem;
  font-weight: 800;
  text-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);

  @media (max-width: 768px) {
    font-size: clamp(2.35rem, 11vw, 4rem);
    line-height: 1.02;
    max-width: 92%;
  }

  @media (max-width: 420px) {
    font-size: clamp(2.15rem, 10.5vw, 3.35rem);
    max-width: 96%;
  }
`;

const Text = styled.p`
  max-width: 640px;
  font-size: 1.12rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.94);
  margin-bottom: 1.8rem;
  text-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);

  @media (max-width: 768px) {
    font-size: 1.02rem;
    line-height: 1.65;
    max-width: 94%;
  }
`;

const AnnouncementBox = styled.div`
  max-width: 640px;
  margin-bottom: 1.8rem;
  padding: 1rem 1.15rem;
  border-radius: 16px;
  background: rgba(255, 248, 225, 0.94);
  color: #3a2418;
  border-left: 6px solid #d4a017;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.24);
`;

const AnnouncementTitle = styled.div`
  font-weight: 800;
  color: #8b1e2d;
  margin-bottom: 0.35rem;
`;

const AnnouncementText = styled.div`
  font-size: 0.98rem;
  line-height: 1.55;
`;

const AnnouncementItem = styled.div`
  &:not(:last-child) {
    margin-bottom: 0.35rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;

  @media (max-width: 420px) {
    gap: 0.75rem;
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 190px;
  padding: 1rem 1.35rem;
  border-radius: 999px;
  background: #8b1e2d;
  color: #fff;
  font-weight: 700;
  box-shadow: 0 14px 30px rgba(139, 30, 45, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 34px rgba(139, 30, 45, 0.42);
    opacity: 0.98;
  }

  @media (max-width: 420px) {
    min-width: 0;
    width: 100%;
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 190px;
  padding: 1rem 1.35rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-weight: 700;
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(3px);
  transition: transform 0.2s ease, background 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.14);
    opacity: 0.98;
  }

  @media (max-width: 420px) {
    min-width: 0;
    width: 100%;
  }
`;

const QuickInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-top: 2rem;

  @media (max-width: 420px) {
    margin-top: 1.6rem;
    gap: 0.65rem;
  }
`;

const InfoChip = styled.div`
  padding: 0.7rem 1rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(4px);
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.94);

  @media (max-width: 420px) {
    width: 100%;
    font-size: 0.9rem;
  }
`;

function Hero({ title, text }) {
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    let isMounted = true;

    getAnnouncements()
      .then((items) => {
        if (isMounted) {
          setAnnouncements(Array.isArray(items) ? items : []);
        }
      })
      .catch((error) => {
        console.error('Hinweise konnten nicht geladen werden:', error);

        if (isMounted) {
          setAnnouncements([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <HeroSection>
      <HeroInner>
        <ContentBox>
          <Eyebrow>Tradition • Gemütlichkeit • Heurigenküche</Eyebrow>

          <Title>{title}</Title>

          <Text>{text}</Text>

          {announcements.length > 0 && (
            <AnnouncementBox>
              <AnnouncementTitle>⚠️ Aktuelle Hinweise</AnnouncementTitle>

              <AnnouncementText>
                {announcements.map((item, index) => (
                  <AnnouncementItem key={`${item.titel || 'hinweis'}-${index}`}>
                    {item.text}
                  </AnnouncementItem>
                ))}
              </AnnouncementText>
            </AnnouncementBox>
          )}

          <ButtonGroup>
            <PrimaryButton to="/speisekarte">Speisekarte ansehen</PrimaryButton>
            <SecondaryButton to="/reservierung">Tisch anfragen</SecondaryButton>
          </ButtonGroup>

          <QuickInfo>
            <InfoChip>Wienerstraße 26, 2620 Neunkirchen</InfoChip>
            <InfoChip>Reservierung per Anfrage</InfoChip>
            <InfoChip>Gemütlich & bodenständig</InfoChip>
          </QuickInfo>
        </ContentBox>
      </HeroInner>
    </HeroSection>
  );
}

export default Hero;